import React, { useEffect, useRef, useState } from "react";
import CompanyHeader from "./CompanyHeader";
import FooterLinks from "../LandingPage/FooterLinks";
import SeoMeta from "../SEO/SeoMeta";

export default function AboutUs() {
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    function updateHeight() {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    }
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="relative min-h-screen w-full bg-white text-black">
      <SeoMeta
        title="About Million2Trillion — The Verified Global Financial Market Ecosystem"
        description="Million2Trillion (M2T), founded by A.G. Senthil Kumar and co-founded by Mr. Satbir Singh Hanspaul, is a verified financial infrastructure platform uniting global traders, investors, educators, coders, influencers, and fintech professionals to Learn, Earn, Connect, Promote, Grow, and Launch together."
        keywords="Million2Trillion, M2T, Verified Financial Market, Financial Professionals, Trading, Investing, Education, Fintech Hub, A.G. Senthil Kumar, Satbir Singh Hanspaul, M2T Founders, Financial Ecosystem, SmartCard Login"
        url="https://million2trillion.com/about-us"
        canonical="https://million2trillion.com/about-us"
      />

      {/* --- FIXED HEADER --- */}
      <CompanyHeader ref={headerRef} activePage="about-us" />

      <div style={{ height: headerHeight }} aria-hidden="true" />

      {/* --- BANNER IMAGE --- */}
      <div className="w-full bg-white">
        <img
          src="/assets/company/Million2Trillion.gif"
          alt="About Million2Trillion"
          className="block w-full"
          style={{ width: "100vw", maxWidth: "100vw", height: "auto" }}
          draggable={false}
        />
      </div>

      {/* --- PAGE CONTENT --- */}
      <main className="relative z-10 mx-auto max-w-7xl space-y-16 px-6 py-16 md:px-20">
        {/* Hero/Intro */}
        <header className="mb-8 space-y-4 text-center">
          <h1 className="text-4xl font-extrabold text-black md:text-5xl">
            Million2Trillion
          </h1>
          <p className="mx-auto max-w-2xl text-lg font-light text-gray-700 md:text-2xl">
            The world’s first verified network connecting financial
            professionals and organizations—empowering every market, everywhere.
          </p>
        </header>

        {/* Quote & Identity */}
        <section className="mx-auto mb-10 max-w-4xl rounded-xl border-l-4 border-purple-600 bg-gradient-to-br from-gray-100 via-gray-50 to-white p-8">
          <p className="text-xl font-semibold italic text-black md:text-2xl">
            “We are laying the foundation for a new era of trusted financial
            markets—connecting every stage from chart to funding.”
          </p>
          <p className="mt-2 text-gray-600">
            At Million2Trillion (M2T), trust, verified identity, and global
            collaboration drive everything we do.
          </p>
        </section>

        {/* Mission, Vision, Values */}
        <section className="mx-auto mb-12 max-w-5xl space-y-12">
          <div className="grid gap-10 text-center md:grid-cols-3">
            <div>
              <h2 className="mb-2 text-2xl font-bold text-purple-400">
                Mission
              </h2>
              <p className="text-gray-700">
                To empower market professionals through a single, trusted
                ecosystem—removing barriers for learning, earning, connecting,
                and building.
              </p>
            </div>
            <div>
              <h2 className="mb-2 text-2xl font-bold text-purple-400">
                Vision
              </h2>
              <p className="text-gray-700">
                Bring together 200+ roles in 29+ categories on a secure,
                identity-powered platform—fostering trust and seamless
                collaboration.
              </p>
            </div>
            <div>
              <h2 className="mb-2 text-2xl font-bold text-purple-400">
                Core Values
              </h2>
              <ul
                className="mx-auto list-inside list-disc text-left text-gray-700"
                style={{ maxWidth: 320 }}
              >
                <li>Integrity & Transparency</li>
                <li>Innovation & Impact</li>
                <li>Collaboration & Empowerment</li>
                <li>Security & Trust</li>
                <li>Diversity & Inclusion</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Problems and Solutions */}
        <section className="mx-auto mb-16 grid max-w-5xl gap-8 md:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-100 via-gray-50 to-white p-6 shadow-lg">
            <h3 className="mb-4 text-xl font-semibold text-red-400">
              Industry Challenges
            </h3>
            <ul className="ml-6 list-disc space-y-2 text-gray-700">
              <li>Fragmented, unverified platforms</li>
              <li>Scams and identity theft risks</li>
              <li>Career growth obstacles</li>
              <li>Lack of global trust</li>
              <li>No secure launchpad for new talent or ideas</li>
            </ul>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-100 via-gray-50 to-white p-6 shadow-lg">
            <h3 className="mb-4 text-xl font-semibold text-green-400">
              How Million2Trillion (M2T) Makes a Difference
            </h3>
            <ul className="ml-6 list-disc space-y-2 text-gray-700">
              <li>Verified, role-based onboarding</li>
              <li>Smart KYC trust economy</li>
              <li>Unified professional and education hubs</li>
              <li>Instant, global networking</li>
              <li>Launchpad for innovation and business growth</li>
            </ul>
          </div>
        </section>

        {/* Global Coverage */}
        <section className="mx-auto max-w-5xl space-y-6 py-10 text-center">
          <h2 className="mb-3 text-3xl font-bold text-purple-300">
            Global Coverage
          </h2>
          <p className="mx-auto max-w-2xl text-gray-700">
            Million2Trillion (M2T) unites <strong>200+ verified roles</strong>{" "}
            in <strong>29+ categories</strong> and{" "}
            <strong>24 market types</strong> worldwide—building a borderless,
            digital-first financial community.
          </p>
          <div className="mt-4 rounded-xl border border-purple-700 bg-gray-50 p-6">
            <p className="text-lg italic text-gray-700">
              Every role. Every market. Verified. Connected. Empowered by
              identity.
            </p>
          </div>
        </section>

        {/* 🟣 Founders Section */}
        <section className="mx-auto max-w-5xl space-y-10 rounded-2xl bg-gradient-to-br from-gray-100 via-white to-gray-100 p-10 text-center shadow-2xl">
          <h2 className="text-3xl font-bold text-purple-400">
            Meet the Founders
          </h2>

          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {/* Co-Founder 2 */}
            <div className="flex flex-col items-center space-y-3">
              <img
                src="/assets/team/Satbir-cofounder.jpg"
                alt="Mr. Satbir Singh Hanspaul – Co-Founder & President of Million2Trillion"
                className="h-44 w-36 rounded-xl object-cover shadow-lg"
              />
              <p className="text-lg font-light italic text-black">
                “We believe in creating lasting global value through verified
                trust and innovation.”
              </p>
              <p className="max-w-sm text-sm text-gray-700">
                <strong>Satbir Singh Hanspaul</strong>, Co-Founder & President,
                is the Group CEO of Hanspaul Group Tanzania — a leading
                industrial conglomerate with investments across Africa. He
                brings visionary leadership, scale experience, and strategic
                investment to Million2Trillion growth.
              </p>
            </div>

            {/* Founder 1 */}
            <div className="flex flex-col items-center space-y-3">
              <img
                src="/assets/team/senthil1.jpg"
                alt="A.G. Senthil Kumar – Founder & CEO of Million2Trillion"
                className="h-44 w-36 rounded-xl object-cover shadow-lg"
              />
              <p className="text-lg font-light italic text-black">
                “We are not just launching a platform—we're building a verified
                financial nation.”
              </p>
              <p className="max-w-sm text-sm text-gray-700">
                <strong>A.G. Senthil Kumar</strong>, Founder & CEO, has 20+
                years of experience in engineering and market innovation. He
                created Million2Trillion (M2T) to unify global financial
                professionals under one verified ecosystem.
              </p>
            </div>
          </div>

          {/* Social links */}
          <div className="mt-6 flex select-none flex-wrap justify-center gap-6 text-blue-600">
            <p className="m-0 cursor-default font-semibold text-gray-700">
              Follow founders:
            </p>
            <a
              href="https://www.linkedin.com/in/satbir-singh-hanspaul-49786a88"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-purple-600"
            >
              Satbir Singh Hanspaul (LinkedIn)
            </a>
            <a
              href="https://www.linkedin.com/in/agsenthilk36011/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-purple-600"
            >
              Senthil Kumar (LinkedIn)
            </a>
          </div>
        </section>
      </main>

      <footer className="mt-36 border-t border-gray-200 bg-white">
        <FooterLinks />
      </footer>
    </div>
  );
}
